import type { components } from '@/shared/api-client/administration/schema';

/**
 * Backend audit action codes, as a string-literal union generated from the OpenAPI
 * schema (ADR 0025). Typing `actionLabels` against this makes a missing or orphaned
 * label a `tsc` error rather than a raw slug rendered at runtime.
 */
export type AuditActionCode = components['schemas']['AuditAction'];

/**
 * Backend permission codes and module names, as string-literal unions generated from
 * the OpenAPI schema (ADR 0025). Typing `roles.permissionLabels` / `roles.modules`
 * against these makes a missing or orphaned label a `tsc` error, replacing the
 * fs-reading parity test that previously guarded the permission edge.
 */
export type PermissionCode = components['schemas']['Permission'];
export type ModuleCode = components['schemas']['Module'];
export type PermissionAreaCode = components['schemas']['PermissionArea'];

export const commonEnUS = {
  repeatedSlots: '① before clocks move back · ② after',
  app: {
    name: 'Amuze',
    tagline: 'Operator console',
  },
  actions: {
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Submit',
  },
  nav: {
    dashboard: 'Dashboard',
    administration: 'Administration',
    franchise: 'Franchise',
    operations: 'Operations',
    users: 'Users',
    usersNeedVenueAccess: 'Some users need venue access',
    roles: 'Roles',
    audit: 'Audit log',
    brand: 'Brand',
    countryPolicies: 'Country policies',
    admissionPolicy: 'Admission policy',
    venues: 'Venues',
    waivers: 'Waivers',
    employees: 'Employees',
    admissions: 'Admissions',
    adultAdmissions: 'Adult admissions',
    selfCheckIn: 'Self check-in',
    adultSelfCheckIn: 'Adult self check-in',
    stagedQueue: 'Staged queue',
    shifts: 'Shifts',
    capacity: 'Capacity',
    customers: 'Customers',
    mvpCardNumbers: 'MVP card numbers',
    rooms: 'Rooms',
    waiver: 'Waiver',
    bookingSettings: 'Booking settings',
    operatingHours: 'Operating hours',
    waiverSigning: 'Waiver signing link',
    bookings: 'Bookings',
    posDevices: 'POS devices',
    posStations: 'POS stations',
    products: 'Products',
    passes: 'Passes',
    discounts: 'Discount codes',
    inventory: 'Inventory',
    franchiseAdmin: 'Franchise administration',
    group: {
      operations: 'Operations',
      catalog: 'Catalog & pricing',
      configuration: 'Configuration',
      selfServe: 'Self-serve',
    },
  },
  admin: {
    sectionHeader: 'Franchise',
    returnToWorkspace: 'Return to workspace',
    forbidden: 'You do not have access to franchise administration.',
    homeTitle: 'Franchise administration',
    homeBody: 'Pick an area from the sidebar to manage the franchise.',
    comingSoon: 'This area is coming soon.',
  },
  landing: {
    title: 'Choose a venue',
    subtitle: 'Select a venue to enter its workspace.',
    empty: 'You do not have access to any venue yet.',
  },
  workspace: {
    sectionHeader: 'At this venue',
    switcher: {
      label: 'Switch venue',
    },
    home: 'Dashboard',
    homeTitle: 'Venue workspace',
    homeBody: 'Pick a feature from the sidebar to work at this venue.',
    dashboard: {
      // Prototype: mocked daily-summary cards + expected-attendance chart. Replaced when wired
      // to real Booking/Visit/Sale data.
      preview: 'Preview data',
      guestsBooked: 'Guests Booked',
      guestsBookedHint: "Expected from today's reservations",
      admissions: 'Admissions Today',
      admissionsHint: 'Guests checked in today',
      revenue: 'Revenue Today',
      revenueHint: "Today's completed sales",
      itemsSold: 'Items Sold Today',
      itemsSoldHint: 'Lines sold across today',
      attendanceTitle: 'Expected Attendance',
      attendanceSubtitle: 'Expected guests per hour today',
      attendanceGuests: 'Guests',
    },
    guard: {
      unknownVenue: 'That venue is not available to you.',
      featureForbidden: 'You do not have access to that feature at this venue.',
    },
  },
  capacityOverview: {
    pageTitle: 'Capacity overview',
    description: 'Live room-by-time capacity snapshot in {{timezone}}.',
    loading: 'Loading capacity overview…',
    columns: {
      row: 'Room / pool',
    },
    poolRow: 'Venue pool - seats left',
    poolHint: '{{total}} seats across active rooms',
    roomHint: '{{count}} seats of room supply',
    supplyLabel: 'Supply',
    closedBanner: 'Closed on this day',
    closedHint: 'No operating hours are set for this weekday.',
    outOfHours: {
      leading: 'Before opening',
      trailing: 'After closing',
      closed: 'Closed-day demand',
      badge: '{{time}} · out of hours',
      ariaLabel:
        'Out-of-hours demand at {{time}}: {{booked}} booked, {{blocked}} blocked, {{mvp}} MVP, {{standard}} Standard',
    },
    actions: {
      previousDay: 'Previous day',
      nextDay: 'Next day',
      addBlock: '+ Block',
    },
    heat: {
      open: 'Open',
      filling: 'Filling',
      near: 'Near full',
      full: 'Full',
    },
    blockDialog: {
      title: 'Create capacity block',
      fields: {
        room: 'Room',
        start: 'Start',
        duration: 'Duration (minutes)',
        blockedSeats: 'Blocked seats',
        reason: 'Reason',
      },
      form: {
        create: 'Create block',
        submitting: 'Creating…',
      },
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load the capacity overview.',
      blank_capacity_block_reason: 'Enter a reason for the block.',
      invalid_capacity_block_window: 'Use a valid block window on the 30-minute grid.',
      invalid_blocked_seats: 'Blocked seats must be a whole number of at least 1.',
      capacity_block_exceeds_room_capacity: 'Blocked seats cannot exceed the room capacity.',
    },
  },
  users: {
    title: 'Users',
    loading: 'Loading users…',
    empty: 'No users found.',
    status: {
      active: 'Active',
      inactive: 'Inactive',
      needsVenues: 'Needs venues',
      ok: 'OK',
    },
    tabs: {
      all: 'All users',
      needs: 'Needs venue access',
    },
    needs: {
      empty: 'No users are waiting on venue access.',
      column: 'Status',
      manageAccess: 'Manage access',
      hideAccess: 'Hide access',
    },
    columns: {
      email: 'Email',
      name: 'Name',
      status: 'Status',
      actions: 'Actions',
    },
    search: {
      label: 'Search users',
      placeholder: 'Search by email or name',
      submit: 'Search',
    },
    pagination: {
      summary: 'Page {{page}} of {{totalPages}} · {{total}} total',
      previous: 'Previous',
      next: 'Next',
    },
    fields: {
      email: 'Email',
      displayName: 'Display name',
      password: 'Initial password',
    },
    actions: {
      create: 'New user',
      manage: 'Manage',
      activate: 'Activate',
      deactivate: 'Deactivate',
      forceLogout: 'Force logout',
      resetPassword: 'Reset password',
    },
    create: {
      title: 'Create user',
      description: 'Add a staff member with an initial password.',
      submit: 'Create user',
      submitting: 'Creating…',
    },
    manage: {
      title: 'Manage user',
      save: 'Save changes',
      dangerZone: 'Account actions',
    },
    confirm: {
      deactivate: 'Deactivate this user? Their active sessions end immediately.',
      forceLogout: 'Force-logout this user from all devices?',
      resetPassword:
        'Reset this user’s password? Their sessions end and they must set a new password on next sign-in.',
    },
    resetPassword: {
      title: 'Temporary password',
      warning:
        'Copy this now — it is shown only once. Give it to the user; they must change it at next sign-in.',
      copy: 'Copy',
      copied: 'Copied',
      dismiss: 'Done',
    },
    venuePosPins: {
      title: 'Venue POS PINs',
      loading: 'Loading POS PINs…',
      empty: 'This user does not hold POS kiosk login at any venue.',
      status: {
        not_issued: 'Not issued',
        issued: 'Issued',
      },
      issue: 'Issue',
      regenerate: 'Regenerate',
      issueAt: 'Issue POS PIN at {{venue}}',
      regenerateAt: 'Regenerate POS PIN at {{venue}}',
      confirmRegenerate:
        'Regenerate this user’s POS PIN at {{venue}}? The current PIN is replaced.',
      revealTitle: 'POS PIN for {{venue}}',
    },
    posPin: {
      warning: 'Copy this now — it is shown only once. Give it to the user for POS kiosk login.',
      copy: 'Copy',
      copied: 'Copied',
      dismiss: 'Done',
    },
    toast: {
      created: 'User created.',
      updated: 'User updated.',
      activated: 'User activated.',
      deactivated: 'User deactivated.',
      forcedLogout: 'User signed out everywhere.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load users.',
      passwordTooShort: 'Password must be at least 12 characters.',
      last_admin_required: 'This is the last administrator and cannot be deactivated.',
      email_taken: 'That email address is already in use.',
      assignment_exists: 'That role is already assigned to this user.',
      pos_kiosk_login_required: 'This user does not hold POS kiosk login at that venue.',
      pos_pin_space_exhausted: 'No POS PIN is free at that venue.',
    },
    assignments: {
      title: 'Role assignments',
      loading: 'Loading assignments…',
      empty: 'No roles assigned.',
      venueAccess: {
        label: 'Venue access',
        adminImplicitAll: 'All venues (Admin)',
        none: 'No selected venues',
        unresolved: 'Venue access required',
        requiredForWorkspace: 'This role needs at least one selected Venue.',
      },
      role: 'Role',
      venues: 'Venues',
      add: 'Add assignment',
      addTitle: 'Add role assignment',
      submit: 'Assign role',
      editVenueAccess: 'Edit venue access',
      revoke: 'Revoke',
      saveVenueAccess: 'Save venue access',
      selectRole: 'Select a role',
      selectVenues: 'Select at least one venue',
      confirmRevoke: 'Revoke this role assignment?',
      effective: {
        label: 'Effective permissions',
        none: 'None of this role’s permissions currently apply.',
        withVenueAccess:
          'Franchise administration permissions apply franchise-wide; Venue workspace permissions apply at the selected Venues.',
        withoutVenueAccess:
          'Franchise administration permissions apply franchise-wide; Venue workspace permissions need selected VenueAccess.',
      },
      toast: {
        created: 'Role assigned.',
        updated: 'Venue access updated.',
        revoked: 'Assignment revoked.',
      },
    },
  },
  roles: {
    title: 'Roles',
    loading: 'Loading roles…',
    empty: 'No roles found.',
    type: {
      system: 'System',
      custom: 'Custom',
    },
    columns: {
      name: 'Name',
      type: 'Type',
      permissions: 'Permissions',
      actions: 'Actions',
    },
    fields: {
      name: 'Role name',
      permissions: 'Permissions',
    },
    actions: {
      create: 'New role',
      manage: 'Manage',
      view: 'View',
      delete: 'Delete role',
      confirmDelete: 'Delete',
      confirmUnresolved: 'Save anyway',
      close: 'Close',
    },
    create: {
      title: 'Create role',
      description: 'Define a custom role and the permissions it grants.',
      submit: 'Create role',
      submitting: 'Creating…',
    },
    edit: {
      title: 'Edit role',
      description: 'Rename the role or change the permissions it grants.',
      submit: 'Save changes',
      submitting: 'Saving…',
    },
    detail: {
      title: 'Role details',
      systemNotice:
        'This is a system role. Its permissions are managed by the platform and cannot be edited.',
    },
    confirm: {
      delete: 'Delete this role? This cannot be undone.',
      unresolvedTitle: 'Confirm unresolved VenueAccess',
      unresolvedDescription:
        '{{count}} assignment(s) have no selected VenueAccess. Franchise administration permissions will still apply, but Venue workspace permissions will apply nowhere until venues are selected.',
    },
    permissions: {
      loading: 'Loading permissions…',
      loadFailed: 'Could not load the permission catalogue.',
    },
    areas: {
      franchise_administration: 'Franchise administration',
      venue_workspace: 'Venue workspace',
    } satisfies Record<PermissionAreaCode, string>,
    modules: {
      IAM: 'Identity & access',
      FRANCHISE: 'Franchise',
      VENUE: 'Venues',
      WORKFORCE: 'Workforce',
      PRODUCTS: 'Products',
      SALES: 'Sales',
      POS: 'POS',
      BOOKING: 'Booking',
      CUSTOMER: 'Customers',
      MULTI_VISIT_PASS: 'Multi-Visit Passes',
      AUDIT: 'Audit',
    } satisfies Record<ModuleCode, string>,
    permissionLabels: {
      'iam.user.manage': 'Manage users',
      'iam.role.manage': 'Manage roles',
      'role_assignment.view': 'View role assignments',
      'role_assignment.create': 'Create role assignments',
      'role_assignment.edit': 'Edit role assignments',
      'role_assignment.delete': 'Delete role assignments',
      'franchise.brand.manage': 'Manage brand profile',
      'franchise.country.manage': 'Manage country policies',
      'venue.manage': 'Manage venues',
      'venue.edit_locale': 'Edit venue locale',
      'venue.view': 'View venues',
      'venue.waiver_template.manage': 'Manage waiver templates',
      'venue.waiver_assignment.manage': 'Manage venue waiver assignments',
      'venue.room.manage': 'Manage rooms',
      'venue.room.view': 'View rooms',
      'venue.capacity_block.manage': 'Manage capacity blocks',
      'venue.capacity_overview.view': 'View capacity overview',
      'venue.booking_settings.manage': 'Manage booking settings',
      'venue.booking_settings.view': 'View booking settings',
      'venue.operating_hours.manage': 'Manage operating hours',
      'venue.operating_hours.view': 'View operating hours',
      'workforce.employee.manage': 'Manage employees',
      'workforce.employee.view': 'View employees',
      'workforce.shift.self': 'Start and end own shift',
      'workforce.shift.manage': 'Manage shifts',
      'workforce.shift.correct': 'Correct shift times',
      'workforce.shift.view': 'View shifts',
      'products.pass.view': 'View passes',
      'products.pass.manage': 'Manage passes',
      'discount.manage': 'Manage discount codes',
      'discount.apply': 'Apply discount codes',
      'discount.manual': 'Apply manual discounts',
      'sales.view': 'View open checks (POS)',
      'sales.complete': 'Take payment and complete a sale',
      'pos.installation.enroll': 'Enroll POS devices',
      'pos.device.manage': 'Manage POS stations',
      'pos.kiosk.login': 'Log in to the POS kiosk with a PIN',
      'booking.view': 'View bookings',
      'booking.create': 'Create bookings',
      'booking.edit': 'Edit bookings',
      'booking.redeem': 'Redeem booking units',
      'booking.cancel': 'Cancel bookings',
      'booking.pay': 'Pay bookings',
      'booking.refund': 'Refund booking units',
      'customer.view': 'View customers',
      'mvp.view': 'View Multi-Visit Pass holders',
      'mvp.sell': 'Sell Multi-Visit Passes',
      'mvp.redeem': 'Redeem Multi-Visit Pass visits',
      'mvp.redemption.undo': 'Undo Multi-Visit Pass visits',
      'mvp.void': 'Void Multi-Visit Passes',
      'mvp.refund': 'Refund Multi-Visit Passes',
      'standard_pass.visit_undo': 'Undo Standard Pass visits',
      'products.inventory.view': 'View inventory',
      'products.inventory.manage': 'Manage inventory',
      'audit.read': 'Read the audit log',
      'permission.view': 'View the permission catalogue',
    } satisfies Record<PermissionCode, string>,
    toast: {
      created: 'Role created.',
      updated: 'Role updated.',
      deleted: 'Role deleted.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load roles.',
      system_role_immutable: 'System roles cannot be edited or deleted.',
      role_in_use: 'This role is assigned to users and cannot be deleted.',
      role_name_taken: 'A role with that name already exists.',
      unknown_permission: 'One or more selected permissions are not recognised.',
      role_update_requires_venue_access_confirmation:
        'Confirm the VenueAccess impact before saving this role.',
    },
  },
  brand: {
    title: 'Brand',
    loading: 'Loading brand profile…',
    preview: 'Preview',
    noLogo: 'No logo',
    logoAlt: 'Franchise logo',
    logoHint: 'PNG, SVG, or JPEG up to 500 KB.',
    fields: {
      displayName: 'Display name',
      primaryColor: 'Primary colour',
      logo: 'Logo',
    },
    actions: {
      save: 'Save changes',
      saving: 'Saving…',
      uploadLogo: 'Upload logo',
      uploading: 'Uploading…',
    },
    toast: {
      updated: 'Brand updated.',
      logoUpdated: 'Logo updated.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load the brand profile.',
      invalid_color: 'Enter a valid hex colour (e.g. #4F46E5).',
      logo_too_large: 'The logo must be 500 KB or smaller.',
      unsupported_logo_type: 'Logos must be PNG, SVG, or JPEG.',
    },
  },
  admissionPolicy: {
    title: 'Admission policy',
    loading: 'Loading admission policy…',
    hint: 'Customers at or above this age are admitted as adults; younger customers go through the child/guardian flow. Front-desk staff cannot change this during check-in.',
    fields: {
      threshold: 'Adult age threshold (years)',
    },
    actions: {
      save: 'Save changes',
      saving: 'Saving…',
    },
    toast: {
      updated: 'Admission policy updated.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load the admission policy.',
      invalid_age_threshold: 'Enter an age of 0 or more.',
    },
  },
  inventory: {
    title: 'Inventory',
    selectVenue: 'Venue',
    searchPlaceholder: 'Search products…',
    loading: 'Loading inventory…',
    empty: 'No products yet.',
    columns: {
      productGroup: 'Product group',
      productName: 'Product name',
      quantity: 'Quantity',
      price: 'Price',
      cogs: 'Cogs',
      margin: 'Margin',
      actions: 'Actions',
    },
    actions: {
      create: 'Create product',
      edit: 'Edit',
    },
    fields: {
      group: 'Product group',
      groupPlaceholder: 'Search or add a group…',
      groupDescription: 'Product group description',
      product: 'Product name',
      quantity: 'Quantity',
      price: 'Price',
      cogs: 'Cogs',
    },
    create: {
      title: 'Create product',
      description: 'Add a product group and its first variant.',
      existingGroup: 'Adding to existing group.',
      submit: 'Create',
      submitting: 'Creating…',
    },
    edit: {
      title: 'Edit product',
      submit: 'Save changes',
      submitting: 'Saving…',
    },
    toast: {
      created: 'Product created.',
      updated: 'Product updated.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load inventory.',
      venue_not_found: 'Venue not found.',
      invalid_name: 'Enter a valid name.',
      invalid_inventory_item_value: 'Enter a valid quantity and amounts.',
    },
  },
  countryPolicies: {
    title: 'Country policies',
    loading: 'Loading country policies…',
    empty: 'No country policies yet.',
    columns: {
      country: 'Country',
      language: 'Language',
      currency: 'Currency',
      timezone: 'Timezone',
      status: 'Status',
      actions: 'Actions',
    },
    status: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    fields: {
      country: 'Country',
      language: 'Default language',
      currency: 'Default currency',
      timezone: 'Default timezone',
    },
    actions: {
      create: 'Add policy',
      edit: 'Edit',
      enable: 'Enable',
      disable: 'Disable',
    },
    reference: {
      loading: 'Loading reference data…',
      loadFailed: 'Could not load reference data.',
    },
    create: {
      title: 'Add country policy',
      description: 'Pick a country and its default language, currency, and timezone.',
      submit: 'Add policy',
      submitting: 'Adding…',
    },
    edit: {
      title: 'Edit {{code}} defaults',
      description: 'Change the default language, currency, or timezone.',
      submit: 'Save changes',
      submitting: 'Saving…',
    },
    confirm: {
      enableTitle: 'Enable policy',
      disableTitle: 'Disable policy',
      enable: 'Enable the country policy for {{code}}?',
      disable:
        'Disable the country policy for {{code}}? Existing venues are unaffected; new ones can no longer be created against it.',
    },
    toast: {
      created: 'Country policy added.',
      updated: 'Country policy updated.',
      enabled: 'Country policy enabled.',
      disabled: 'Country policy disabled.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load country policies.',
      country_policy_exists: 'A policy for that country already exists.',
      invalid_country_code: 'Select a valid country.',
      invalid_language: 'Select a valid language.',
      invalid_currency: 'Select a valid currency.',
      invalid_timezone: 'Select a valid timezone.',
    },
  },
  products: {
    title: 'Products',
    description: 'Passes this venue presells, priced in {{currency}}.',
    loading: 'Loading passes…',
    empty: 'No passes yet. Use Create to add one.',
    filters: {
      all: 'All',
      STANDARD: 'Standard',
      SESSION: 'Session',
      RECURRING: 'Recurring',
      MULTI_VISIT: 'Multi-Visit Passes',
    },
    sections: { passes: 'Passes' },
    types: {
      STANDARD: 'Standard',
      SESSION: 'Session',
      RECURRING: 'Recurring',
    },
    columns: {
      name: 'Name',
      type: 'Type',
      code: 'Code',
      variations: 'Variations',
      status: 'Status',
      actions: 'Actions',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
    },
    actions: {
      create: 'Create',
      save: 'Save pass',
      cancel: 'Cancel',
      edit: 'Edit',
      deactivate: 'Deactivate',
      reactivate: 'Reactivate',
      addVariation: 'Add variation',
      removeVariation: 'Remove',
      back: 'Back to products',
    },
    variationStatus: {
      active: 'Active',
      inactive: 'Inactive',
    },
    form: {
      createTitle: 'Create {{type}} pass',
      editTitle: 'Edit {{type}} pass',
      generalDetails: 'General details',
      name: 'Name',
      namePlaceholder: 'e.g. HOLIDAY CAMP PASS',
      description: 'Description',
      rooms: 'Rooms',
      allRooms: 'All rooms (includes rooms added later)',
      pickRooms: 'Rooms this pass covers',
      noRooms: 'This venue has no rooms yet.',
      duration: {
        label: 'Session duration',
        '30': '30 min',
        '60': '60 min',
        '90': '90 min',
        '120': '2 hours',
      },
      variations: 'Variations',
      variationName: 'Variation name',
      variationNamePlaceholder: 'e.g. Weekly',
      price: 'Price',
      hours: 'Hours',
      variationActive: 'Active',
      policy: 'Waiver & deposit',
      waiverRequired: 'Waiver required',
      depositAllowed: 'Allow deposit',
      depositType: 'Deposit type',
      depositPercentage: 'Percentage',
      depositFixed: 'Fixed amount',
      depositValue: 'Deposit value',
      depositValuePercent: 'Deposit value (%)',
      depositValueFixed: 'Deposit value ({{currency}})',
      availability: {
        legend: 'Sales availability',
        validDays: 'Valid days',
        exclusionDays: 'Exclusion days',
        allDaysHint: 'Leave every weekday unchecked to allow all days.',
        startDate: 'Start date',
        endDate: 'End date',
        weekday: {
          MONDAY: 'Mon',
          TUESDAY: 'Tue',
          WEDNESDAY: 'Wed',
          THURSDAY: 'Thu',
          FRIDAY: 'Fri',
          SATURDAY: 'Sat',
          SUNDAY: 'Sun',
        },
      },
      options: {
        legend: 'Additional options',
        hideFromOnlineSale: 'Hide from online sale',
        hideFromOnlineSaleHint: 'Sold in-venue only; excluded from the online catalogue.',
        bookingReminder: 'Booking reminder',
        bookingReminderHint: 'The customer is reminded a set number of days before their booking.',
        bookingReminderDays: 'Days before the booking',
        confirmationRequired: 'Confirmation required',
        confirmationRequiredHint: 'The booking must be confirmed before it becomes active.',
      },
    },
    toast: {
      created: 'Pass created.',
      updated: 'Pass updated.',
      deactivated: 'Pass deactivated.',
      reactivated: 'Pass reactivated.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load passes.',
      notFound: 'This pass no longer exists.',
      invalid_name: 'Enter a name for the pass.',
      invalid_variant_name: 'Enter a name for each variation.',
      variants_required: 'Add at least one variation.',
      invalid_deposit: 'Enter a valid deposit (a percentage of 1–100, or an amount above 0).',
      last_active_variant: 'Keep at least one active variation, or deactivate the whole pass.',
      rooms_required: 'Pick at least one room, or choose all rooms.',
      unknown_room: 'One of the selected rooms is not at this venue. Reload and try again.',
      invalid_room_id: 'One of the selected rooms is invalid. Reload and try again.',
      variant_not_found: 'One of the variations no longer exists. Reload and try again.',
      invalid_session_duration: 'Pick a session duration of 30, 60, 90, or 120 minutes.',
      invalid_availability_window: 'The availability end date must not be before the start date.',
      invalid_booking_reminder:
        'Enter how many days before the booking the reminder is sent (at least 1).',
      invalid_pass_hours: 'Enter whole hours of at least 1 for each variation.',
      standard_pass_option_not_applicable:
        'A Standard Pass has no deposit, online-sale hiding, booking reminder or confirmation.',
    },
  },
  multiVisitPasses: {
    createItem: 'Multi-Visit Pass',
    loading: 'Loading Multi-Visit Passes…',
    empty: 'No Multi-Visit Passes yet. Use Create to add one.',
    columns: {
      name: 'Name',
      optionsOnSale: 'Options on sale',
      waiver: 'Waiver',
      status: 'Status',
      actions: 'Actions',
    },
    waiverRequired: 'Required',
    status: {
      active: 'Active',
      inactive: 'Inactive',
    },
    actions: {
      edit: 'Edit',
      view: 'View',
      deactivate: 'Deactivate',
      reactivate: 'Reactivate',
      addOption: 'Add option',
      removeOption: 'Remove option',
      deleteDefinition: 'Delete Multi-Visit Pass',
      confirmDelete: 'Delete this Multi-Visit Pass and all its unsold options?',
      save: 'Save',
      cancel: 'Cancel',
      back: 'Back to products',
    },
    form: {
      createTitle: 'Create Multi-Visit Pass',
      editTitle: 'Edit Multi-Visit Pass',
      viewTitle: 'Multi-Visit Pass',
      name: 'Name',
      namePlaceholder: 'e.g. Play hours',
      waiverRequired: 'Each guest needs a waiver',
      active: 'Active',
      inactiveHint:
        'Inactive: no option is sold at the POS. Multi-Visit Passes already sold stay redeemable until they expire.',
      options: 'Options',
      optionName: 'Button name',
      optionNamePlaceholder: 'e.g. MVP 10',
      hours: 'Hours',
      sold: 'Sold',
      soldCount: '{{count}} sold',
      soldHoursHint: 'For a different number of hours, add a new option.',
      price: 'Price ({{currency}})',
      perHour: '{{amount}} {{currency}} / h',
      validFor: 'Valid for',
      validityAmount: 'Validity amount',
      validityUnit: 'Validity unit',
      usableOn: 'Usable on',
      everyDay: 'Every day',
      onSale: 'On sale',
      preview: 'POS preview',
      previewEmpty: 'Nothing on sale.',
      unnamed: 'Unnamed',
      tileHours: '{{hours}} h',
    },
    units: {
      DAYS: 'days',
      MONTHS: 'months',
    },
    validity: {
      DAYS_one: '{{count}} day',
      DAYS_other: '{{count}} days',
      MONTHS_one: '{{count}} month',
      MONTHS_other: '{{count}} months',
    },
    validation: {
      nameRequired: 'Enter a name for the Multi-Visit Pass.',
      optionsRequired: 'Add at least one option.',
      optionNameRequired: 'Name the button the cashier will press.',
      optionNameDuplicate: 'Another option on sale already has this name.',
      hours: 'Whole hours, at least 1.',
      price: 'Set a price above 0.',
      validity: 'At least 1.',
      weekdays: 'Pick at least one day, or Every day.',
      fixErrors: 'Fix the highlighted fields before saving.',
    },
    toast: {
      created: 'Multi-Visit Pass created.',
      updated: 'Multi-Visit Pass updated.',
      deleted: 'Multi-Visit Pass deleted.',
      deactivated: 'Multi-Visit Pass deactivated.',
      reactivated: 'Multi-Visit Pass reactivated.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      mvp_name_blank: 'Enter a name for the Multi-Visit Pass.',
      mvp_options_required: 'Add at least one option.',
      mvp_option_name_blank: 'Name every option.',
      mvp_option_name_duplicate: 'Two options on sale share a name. Rename one.',
      mvp_option_hours_invalid: 'Option hours must be a whole number of at least 1.',
      mvp_option_price_required: 'Set a price above 0 for every option.',
      mvp_option_validity_invalid: 'Option validity must be at least 1.',
      mvp_option_not_found: 'One of the options no longer exists. Reload and try again.',
      mvp_sold_option_hours_locked:
        'Sold option hours are locked. Add a new option for a different size.',
      mvp_sold_option_delete_forbidden: 'Retire a sold option by turning On sale off.',
      mvp_sold_definition_delete_forbidden:
        'Deactivate a Multi-Visit Pass with sales instead of deleting it.',
    },
  },
  mvpCustomers: {
    noAllowedHistory: 'There is no other customer history you are allowed to see.',
    generate: {
      count: 'Card count',
      download: 'Generate card numbers',
      generating: 'Generating…',
      failed: 'Could not generate card numbers.',
    },
    title: 'Customers',
    description: 'Find a customer by name, email, phone or MVP card.',
    searchLabel: 'Search customers',
    searchPlaceholder: 'Name, email, phone or scan card',
    search: 'Search',
    searching: 'Searching…',
    noMatches: 'No customer matches “{{query}}”.',
    liveHoursPill: '{{hours}} h',
    pickHint: 'Search for a customer to see their profile and available history.',
    loading: 'Loading customer…',
    loadFailed: 'Could not load this customer.',
    searchFailed: 'Could not search customers.',
    headline: '{{hours}} h',
    headlineCaption_one: 'left across {{count}} live pass',
    headlineCaption_other: 'left across {{count}} live passes',
    tabs: {
      passes: 'Multi-Visit Passes ({{count}})',
      cards: 'MVP cards ({{count}})',
      visits: 'Visits',
      standardVisits: 'Standard Pass visits',
    },
    status: {
      LIVE: 'Live',
      USED_UP: 'Used up',
      EXPIRED: 'Expired',
      VOIDED: 'Voided',
      REFUNDED: 'Refunded',
    },
    cardStatus: {
      ACTIVE: 'Active',
      DEACTIVATED: 'Deactivated',
    },
    balance: '{{left}} / {{hours}} h',
    lostHours_one: '{{count}} h lost',
    lostHours_other: '{{count}} h lost',
    notValidToday: 'Not valid today',
    expiresIn_one: 'Expires {{date}} (in {{count}} day)',
    expiresIn_other: 'Expires {{date}} (in {{count}} days)',
    expiresToday: 'Expires {{date}} (today)',
    expired: 'Expired {{date}}',
    expires: 'Expires {{date}}',
    everyDay: 'Every day',
    sold: 'sold {{date}} on {{receipt}}',
    soldBy: 'sold {{date}} on {{receipt}} by {{seller}}',
    noPasses: 'No Multi-Visit Passes at this venue.',
    cardBound: 'bound {{date}}',
    noCards: 'No MVP card. Found by name or phone at the POS.',
    voidRefund: {
      void: 'Void MVP',
      refund: 'Record refund',
      voidTitle: 'Void {{name}}',
      refundTitle: 'Record refund for {{name}}',
      reason: 'Reason',
      amount: 'Refund amount (up to {{price}} {{currency}})',
      method: 'Paid back in',
      methods: { cash: 'Cash', card_manual: 'Card' },
      methodRecord: { cash: 'in cash', card_manual: 'by card' },
      confirm: 'Confirm',
      cancel: 'Cancel',
      failed: 'Could not record this action.',
      voidRecord: 'Voided {{date}} by {{by}}: {{reason}}',
      refundRecord: 'Refund {{amount}} {{currency}} recorded {{date}} by {{by}}: {{reason}}',
      refundRecordWithMethod:
        'Refund {{amount}} {{currency}} {{method}} recorded {{date}} by {{by}}: {{reason}}',
    },
    visits: {
      loading: 'Loading visits…',
      loadFailed: 'Could not load visits.',
      empty: 'No MVP visits at this venue.',
      guestsStay: '{{guests}} guests × {{hours}} h',
      window: 'MVP visit window {{start}}–{{end}}',
      guestWaivers: 'Guest waivers',
      signedBy: 'Signed by {{name}}',
      sale: 'Sale {{receipt}}',
      receiptUnavailable: 'receipt unavailable',
      servedBy: 'served by {{server}}',
      unknownOperator: 'Unknown user',
      draw: '{{name}}: {{hours}} h drawn',
      undone: 'Undone',
      undoStamp: 'Undone {{time}} by {{by}} · {{reason}}',
      reason: 'Reason for undo',
      undo: 'Undo visit',
      confirmUndo: 'Confirm undo',
      cancel: 'Cancel',
      unavailable: 'A drawn MVP was voided or refunded. This visit cannot be undone.',
      undoFailed: 'Could not undo this visit.',
    },
    standardVisits: {
      loading: 'Loading Standard Pass visits…',
      loadFailed: 'Could not load Standard Pass visits.',
      empty: 'No Standard Pass visits at this venue.',
      passVariant: '{{pass}} · {{variant}}',
      guests_one: '{{count}} guest',
      guests_other: '{{count}} guests',
      window: 'Window {{start}}–{{end}}',
      unknownOperator: 'Unknown user',
      undone: 'Undone',
      undoStamp: 'Undone {{time}} by {{by}} · {{reason}}',
      reason: 'Reason for undo',
      noMoneyBack: 'Undo frees the seats. The Sale and its payment stay as they are.',
      undo: 'Undo visit',
      confirmUndo: 'Confirm undo',
      cancel: 'Cancel',
      undoFailed: 'Could not undo this visit.',
    },
  },
  mvpCardNumbers: {
    title: 'MVP card numbers',
    description:
      'Generate fresh card numbers for the printer. Download the CSV before printing card stock.',
  },
  discounts: {
    title: 'Discount codes',
    loading: 'Loading discount codes…',
    empty: 'No discount codes yet. Use New to create one.',
    columns: {
      code: 'Code',
      value: 'Value',
      scope: 'Scope',
      status: 'Status',
      redemptions: 'Redemptions',
      actions: 'Actions',
    },
    status: {
      live: 'Live',
      scheduled: 'Scheduled',
      expired: 'Expired',
      exhausted: 'Exhausted',
      off: 'Off',
    },
    scope: {
      ITEM: 'Item',
      BILL: 'Bill',
    },
    venueScope: {
      VENUE: 'One venue',
      FRANCHISE: 'Every venue',
    },
    days: {
      MONDAY: 'Mon',
      TUESDAY: 'Tue',
      WEDNESDAY: 'Wed',
      THURSDAY: 'Thu',
      FRIDAY: 'Fri',
      SATURDAY: 'Sat',
      SUNDAY: 'Sun',
    },
    actions: {
      create: 'New discount code',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      deactivate: 'Deactivate',
      reactivate: 'Reactivate',
      back: 'Back to discount codes',
    },
    confirm: {
      deactivateTitle: 'Deactivate this discount code?',
      deactivateDescription:
        '"{{code}}" will stop working at the till immediately, including on sales already in progress. This cannot be undone from here — reactivate it later if the campaign resumes.',
    },
    toast: {
      created: 'Discount code created.',
      updated: 'Discount code updated.',
      deactivated: 'Discount code deactivated.',
      reactivated: 'Discount code reactivated.',
    },
    create: {
      title: 'Create discount code',
    },
    edit: {
      title: 'Edit {{code}}',
    },
    form: {
      identity: 'Identity',
      code: 'Code',
      codePlaceholder: 'e.g. SUMMER10',
      generate: 'Generate',
      scope: 'Applies to',
      value: 'Value',
      valueKind: 'Type',
      percentage: 'Percentage',
      fixedAmount: 'Fixed amount',
      currency: 'Currency',
      reach: 'Reach',
      venueScope: 'Venue reach',
      venue: 'Venue',
      venuePlaceholder: 'Select a venue…',
      allowlist: 'Sellable item allowlist',
      allowlistPlaceholder: 'Comma-separated item IDs — leave empty for every discountable item',
      allowlistHint: 'Leave empty to let the code apply to anything discountable.',
      minimumSpend: 'Minimum spend',
      validity: 'Validity',
      startsAt: 'Starts',
      endsAt: 'Ends',
      validDays: 'Valid days',
      validFromTime: 'Valid from (time)',
      validUntilTime: 'Valid until (time)',
      limits: 'Limits',
      redemptionCap: 'Redemption cap',
      redemptionCapHint: 'Leave empty for an open promotion with no ceiling.',
      discoverable: 'Discoverable by staff',
      discoverableOnNotice:
        'Staff can find this code by searching — do not use for a private voucher.',
      discoverableOffNotice: 'Off by default: staff must be told the code by the customer.',
    },
    errors: {
      generic: 'Something went wrong. Try again.',
      discount_code_duplicate: 'A discount code with this code already exists.',
      discount_code_not_found: 'This discount code no longer exists.',
      discount_code_required: 'Enter a code, or generate one.',
      discount_scope_required: 'Choose whether this code applies to an item or the bill.',
      discount_value_required: 'Enter a percentage or a fixed amount.',
      discount_percentage_basis_points_invalid: 'Enter a percentage between 0 and 100.',
      discount_fixed_amount_invalid: 'Enter a valid fixed amount.',
      discount_fixed_amount_currency_mismatch:
        'The fixed amount currency must match the venue currency.',
      discount_fixed_amount_currency_unknown: 'No venues exist yet to derive a currency from.',
      discount_fixed_amount_multi_currency_scope:
        'A fixed-amount code spanning more than one currency is not allowed — pick one venue or use a percentage.',
      discount_venue_scope_required: 'Choose whether this code reaches one venue or every venue.',
      discount_venue_required: 'Select a venue for a venue-scoped code.',
      discount_venue_must_be_empty: 'A franchise-wide code must not name a venue.',
      discount_window_invalid: 'The end of the validity window must not be before the start.',
      discount_time_window_incomplete: 'Set both a from-time and an until-time, or neither.',
      discount_time_window_invalid: 'The until-time must not be before the from-time.',
      discount_redemption_cap_invalid: 'Enter a redemption cap of at least 1, or leave it empty.',
      discount_minimum_spend_invalid: 'Enter a minimum spend of at least 0, or leave it empty.',
    },
  },
  bookingList: {
    title: 'Bookings',
    description: "Today's reservations at this venue.",
    loading: 'Loading bookings…',
    partySize: 'Party of {{count}}',
    newBooking: 'New booking',
    empty: {
      title: 'No bookings today',
      body: 'Bookings made for this venue will appear here.',
    },
    filters: {
      label: 'Filter bookings',
      today: 'Today',
      upcoming: 'Upcoming',
      onDate: 'Pick a date',
    },
    status: {
      booked: 'Booked',
      seated: 'Seated',
      cancelled: 'Cancelled',
      paid: 'Paid',
    },
    pay: {
      cash: 'Pay with cash',
      card: 'Pay with card',
      success: 'Booking paid.',
    },
    edit: {
      action: 'Edit booking',
      title: 'Edit booking',
      itemsHint: 'Update the details and passes for this reservation.',
      submit: 'Save booking',
      success: 'Booking updated.',
    },
    cancel: {
      action: 'Cancel booking',
      success: 'Booking cancelled.',
    },
    revoke: {
      title: 'Revoke waiver',
      description: 'Record why this linked waiver record should no longer be used.',
      reason: 'Revocation reason',
      submit: 'Confirm revocation',
      submitting: 'Revoking…',
      success: 'Waiver revoked.',
    },
    units: {
      title: 'Passes (units)',
      cancelAction: 'Cancel',
      refundAction: 'Mark refunded',
      cancelSuccess: 'Unit cancelled.',
      refundSuccess: 'Unit marked refunded.',
      waiver: {
        title: 'Waiver',
        signedBy: 'Signed by {{signer}}',
        currentStatusLabel: 'Current status',
        required: 'Waiver required, but none is linked yet.',
        none: 'Not available',
        revoke: 'Revoke waiver',
        currentStatus: {
          VALID: 'Valid',
          EXPIRED: 'Expired',
          REVOKED: 'Revoked',
        },
      },
      status: {
        available: 'Available',
        redeemed: 'Redeemed',
        expired: 'Expired',
        cancelled: 'Cancelled',
        refunded: 'Refunded',
      },
    },
    detail: {
      title: 'Booking',
      description: 'The saved reservation and its passes.',
      loading: 'Loading booking…',
      customer: 'Customer',
      reference: 'Reference',
      phone: 'Phone',
      email: 'Email',
      partySize: 'Party',
      status: 'Status',
      items: 'Passes',
      total: 'Total',
    },
    create: {
      title: 'New booking',
      firstName: 'First name',
      lastName: 'Last name',
      phone: 'Phone (optional)',
      email: 'Email (optional)',
      partySize: 'Party size',
      items: 'Passes',
      itemsHint: 'Choose at least one pass and set how many.',
      noItems: 'No passes available at this venue yet.',
      loadingItems: 'Loading passes…',
      quantity: 'Quantity',
      slotDate: 'Date',
      slotTime: 'Start time',
      slotTimePlaceholder: 'Choose a time',
      notOnSale: 'Not on sale on {{date}}',
      slotDateClosed: 'Venue closed on this date.',
      slotHoursUnconfigured: 'Operating hours are not configured for this venue.',
      submit: 'Create booking',
      cancel: 'Cancel',
      success: 'Booking created.',
    },
    errors: {
      blank_booking_name: 'First and last name are required.',
      invalid_party_size: 'Party size must be at least 1.',
      booking_items_required: 'Add at least one pass.',
      invalid_booking_item_quantity: 'Each pass needs a quantity of at least 1.',
      unknown_booking_item: 'A selected pass is not bookable at this venue.',
      missing_session_slot: 'Choose an exact date and start time for each session pass.',
      invalid_session_slot_grid: 'Session start times must be on the :00 or :30 mark.',
      session_slot_in_past: 'Choose a session slot that is strictly in the future.',
      session_slot_beyond_booking_horizon: "That date is beyond the venue's booking horizon.",
      session_slot_outside_operating_hours:
        'Choose a session slot within the venue operating hours.',
      pass_not_on_sale:
        'This pass is not on sale on the requested date. Choose another pass or date.',
      unexpected_session_slot: 'Only a session pass may have a date and start time.',
      inconsistent_session_slot: 'Choose a complete date and start time for the session pass.',
      booking_not_payable: 'This booking can no longer be paid.',
      booking_not_editable: 'This booking can no longer be edited.',
      booking_not_cancellable: 'This booking can no longer be cancelled.',
      booking_item_unit_not_cancellable: 'This unit can no longer be cancelled.',
      booking_item_unit_cancellation_cutoff_passed:
        "This unit is past the venue's cancellation cutoff.",
      booking_item_unit_not_refundable: 'This unit cannot be marked refunded.',
      booking_item_quantity_below_redeemed:
        'Quantity cannot drop below the number of redeemed units.',
      session_slot_change_with_redeemed_units:
        'This slot cannot change while it has redeemed units.',
      waiverReasonRequired: 'Enter a revocation reason.',
      waiverRevokeGeneric: 'Could not revoke the waiver. Please try again.',
      generic: 'Could not create the booking. Please try again.',
      payGeneric: 'Could not take the payment. Please try again.',
      editGeneric: 'Could not save the booking. Please try again.',
      cancelGeneric: 'Could not cancel the booking. Please try again.',
      unitCancelGeneric: 'Could not cancel the unit. Please try again.',
      unitRefundGeneric: 'Could not mark the unit refunded. Please try again.',
    },
  },
  venues: {
    title: 'Venues',
    loading: 'Loading venues…',
    empty: 'No venues yet.',
    columns: {
      name: 'Name',
      country: 'Country',
      locale: 'Locale',
      activeWaiver: 'Active waiver',
      actions: 'Actions',
    },
    fields: {
      name: 'Name',
      country: 'Country',
      language: 'Language',
      currency: 'Currency',
      timezone: 'Timezone',
      slotGranularity: 'Slot granularity',
      slotGranularity30: '30 minutes',
      slotGranularity60: '60 minutes',
    },
    actions: {
      create: 'Add venue',
      view: 'View',
      edit: 'Edit name',
      changeLocale: 'Change locale',
    },
    reference: {
      loading: 'Loading countries…',
      loadFailed: 'Could not load countries.',
      noCountries: 'No enabled country is available. Enable a country policy first.',
    },
    create: {
      title: 'Add venue',
      description: 'Name the venue and pick an enabled country; its locale is snapshotted now.',
      snapshotPreview: 'Locale snapshotted at create:',
      submit: 'Add venue',
      submitting: 'Adding…',
      back: 'Back to venues',
      previous: 'Previous',
      next: 'Next',
      steps: {
        label: 'Add venue steps',
        basic: {
          title: 'Basic data',
          description: 'Name and country',
        },
        schedule: {
          title: 'Schedule',
          description: 'Granularity and hours',
        },
      },
      basic: {
        description:
          'Name the venue and pick the country it operates in. The country decides language, currency and timezone, and those are snapshotted at creation.',
        nameHint: 'Shown to staff in the venue switcher.',
        countryPlaceholder: 'Select country',
        countryHint: 'Cannot be changed casually.',
        snapshotEmpty: 'Pick a country to preview the snapshot.',
      },
      schedule: {
        description:
          'Booking slots are cut on the granularity below, so opening and closing times are offered on the same grid.',
        granularityMinutes: '{{minutes}} min',
      },
    },
    detail: {
      description: 'Snapshotted locale for this venue.',
      waiverSigningLink: {
        title: 'Waiver signing link',
        description:
          'Use this static public link or QR code on front-desk signage so guests can sign before check-in.',
        qrCodeAriaLabel: 'Waiver signing QR code',
      },
    },
    edit: {
      title: 'Edit venue name',
      description: 'Rename the venue. Its locale is unchanged.',
      submit: 'Save changes',
      submitting: 'Saving…',
    },
    locale: {
      title: 'Change locale for {{name}}',
      warning:
        'This overrides the locale snapshotted at create time and affects how this venue formats language, currency, and time. It is audited.',
      confirm: 'I understand this changes the venue’s locale.',
      submit: 'Change locale',
      submitting: 'Changing…',
    },
    toast: {
      created: 'Venue added.',
      updated: 'Venue updated.',
      localeChanged: 'Venue locale changed.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load venues.',
      country_not_available: 'That country is not enabled. Enable its country policy first.',
      invalid_name: 'Enter a venue name.',
      invalid_locale: 'Language, currency, and timezone are all required.',
    },
    waivers: {
      pageTitle: 'Waiver',
      scopedDescription: "{{name}}'s active waiver and sign-in link.",
      activeLoading: 'Loading active waiver…',
      activeLoadFailed: 'Could not load the active waiver.',
      activeTemplate: 'Active template',
      activeTemplateFallback: 'Assigned waiver',
      requiredAcknowledgement: '● required —',
      optionalAcknowledgement: '○ optional —',
      title: 'Waiver templates',
      description:
        'Author append-only waiver template versions and assign one active version per venue.',
      loading: 'Loading waiver templates…',
      empty: 'No waiver templates yet.',
      unassigned: 'No active waiver',
      noAcknowledgements: 'No acknowledgement items yet.',
      acknowledgementsHint:
        'Add optional or required boolean acknowledgements beneath the waiver body.',
      columns: {
        name: 'Template',
        latestVersion: 'Latest version',
        acknowledgements: 'Ack items',
        assignedVenues: 'Assigned venues',
      },
      fields: {
        name: 'Template name',
        body: 'Waiver body',
        acknowledgements: 'Acknowledgement items',
        ackText: 'Acknowledgement text',
        required: 'Required',
        template: 'Template',
        version: 'Version',
      },
      actions: {
        create: 'Add waiver template',
        newVersion: 'New version',
        change: 'Change waiver',
        assign: 'Assign waiver',
        back: 'Back to waivers',
        addAcknowledgement: 'Add acknowledgement',
        removeAcknowledgement: 'Remove',
      },
      detail: {
        latestVersion: 'Latest version',
        assignments: 'Assigned at {{count}} venue(s).',
        history: 'Version history',
      },
      create: {
        title: 'Add waiver template',
        description: 'Create a new waiver template and its first assignable version.',
        submit: 'Save template',
        submitting: 'Saving…',
      },
      version: {
        title: 'New version for {{name}}',
        description: 'Editing the body or acknowledgements creates a new append-only version.',
        submit: 'Save version',
        submitting: 'Saving…',
      },
      assign: {
        title: 'Assign waiver for {{name}}',
        description: 'Choose the active waiver template version for this venue.',
        preview: '{{count}} acknowledgement item(s) on the selected version.',
        submit: 'Assign waiver',
        submitting: 'Assigning…',
      },
      toast: {
        created: 'Waiver template created.',
        versionCreated: 'Waiver template version created.',
        assigned: 'Venue waiver assignment updated.',
      },
      errors: {
        loadFailed: 'Could not load waiver templates.',
      },
    },
    rooms: {
      title: 'Rooms · {{name}}',
      pageTitle: 'Rooms',
      description: 'Configure rooms and their capacity for this venue.',
      scopedDescription: 'Configure rooms and capacity for {{name}}.',
      loading: 'Loading rooms…',
      empty: 'No rooms yet.',
      columns: {
        name: 'Name',
        capacity: 'Capacity',
        status: 'Status',
        actions: 'Actions',
      },
      fields: {
        name: 'Name',
        capacity: 'Capacity',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
      actions: {
        create: 'Add room',
        edit: 'Edit',
        activate: 'Activate',
        deactivate: 'Deactivate',
      },
      form: {
        create: 'Add room',
        save: 'Save changes',
        submitting: 'Saving…',
      },
      toast: {
        created: 'Room added.',
        updated: 'Room updated.',
        activated: 'Room activated.',
        deactivated: 'Room deactivated.',
      },
      errors: {
        generic: 'Something went wrong. Please try again.',
        loadFailed: 'Could not load rooms.',
        invalid_name: 'Enter a room name.',
        invalid_capacity: 'Capacity must be a whole number of at least 1.',
        duplicate_name: 'A room with that name already exists in this venue.',
      },
    },
    bookingSettings: {
      pageTitle: 'Booking settings',
      scopedDescription:
        'Configure the booking horizon and cancellation cutoff for {{name}}. These are not enforced yet.',
      loading: 'Loading booking settings…',
      fields: {
        bookingHorizonDays: 'Booking horizon (days)',
        bookingHorizonDaysHint:
          'How many days ahead a booking may take a session slot. Default 90.',
        cancellationCutoffMinutes: 'Cancellation cutoff (minutes)',
        cancellationCutoffMinutesHint:
          'How close to a slot an unpaid unit may still be cancelled. Default 60.',
      },
      form: {
        save: 'Save changes',
        submitting: 'Saving…',
      },
      toast: {
        updated: 'Booking settings updated.',
      },
      errors: {
        generic: 'Something went wrong. Please try again.',
        loadFailed: 'Could not load booking settings.',
        invalid_booking_horizon_days: 'Booking horizon must be a whole number of at least 1 day.',
        invalid_cancellation_cutoff_minutes:
          'Cancellation cutoff must be a whole number of at least 1 minute.',
      },
    },
    operatingHours: {
      pageTitle: 'Operating hours',
      scopedDescription:
        'Configure the weekday opening hours for {{name}} on its {{minutes}}-minute slot grid. Every sale must fit inside them; close at 00:00 to stay open until midnight.',
      loading: 'Loading operating hours…',
      weekdays: {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
      },
      fields: {
        openTime: '{{day}} opening time',
        closeTime: '{{day}} closing time',
      },
      picker: {
        hoursTitle: 'Operating hours',
        copyMonday: 'Copy Monday to all open days',
        openToggle: '{{day}} open',
        closed: 'Closed',
        timePlaceholder: '--:--',
        timeSeparator: 'to',
        midnightClose: '00:00 (midnight)',
      },
      form: {
        save: 'Save changes',
        submitting: 'Saving…',
      },
      toast: {
        updated: 'Operating hours updated.',
      },
      errors: {
        generic: 'Something went wrong. Please try again.',
        loadFailed: 'Could not load operating hours.',
        missing_interval: 'Every open day needs both an opening and closing time.',
        invalid_interval: 'Each open day must close after it opens.',
        off_grid_interval: "Opening and closing times must lie on the venue's slot grid.",
        invalid_venue_operating_hours:
          'Operating hours must contain one valid interval for each weekday.',
      },
    },
  },
  posDevices: {
    title: 'POS devices',
    description: 'Create, enroll, and revoke trusted POS kiosk terminals for this venue.',
    loading: 'Loading POS devices…',
    empty: 'No POS devices yet.',
    nameLabel: 'Device name',
    namePlaceholder: 'Till 1',
    create: 'Create device',
    revoke: 'Revoke',
    copyCode: 'Copy code',
    enrollmentCodeTitle: '{{name}} enrollment code',
    enrollmentCodeHint:
      'Use this code on the kiosk within about 10 minutes. It is shown only once.',
    pendingUntil: 'Enrollment code expires at {{time}}.',
    enrolledAt: 'Enrolled {{date}}.',
    revokedAt: 'Revoked {{date}}.',
    noTimestamp: 'No timestamp available.',
    state: {
      PENDING: 'Pending',
      ACTIVE: 'Active',
      REVOKED: 'Revoked',
    },
    toast: {
      copied: 'Enrollment code copied.',
      revoked: '{{name}} revoked.',
    },
  },
  posStations: {
    pageTitle: 'POS stations',
    description: 'Configure logical selling stations (e.g. "Bar", "Entrance") for this venue.',
    loading: 'Loading POS stations…',
    empty: 'No POS stations yet.',
    showArchived: 'Show archived',
    columns: {
      name: 'Name',
      status: 'Status',
      actions: 'Actions',
    },
    fields: {
      name: 'Name',
    },
    status: {
      active: 'Active',
      archived: 'Archived',
    },
    actions: {
      create: 'Add POS station',
      edit: 'Edit',
      archive: 'Archive',
      restore: 'Restore',
    },
    form: {
      create: 'Add POS station',
      save: 'Save changes',
      submitting: 'Saving…',
    },
    toast: {
      created: 'POS station added.',
      renamed: 'POS station renamed.',
      archived: 'POS station archived.',
      restored: 'POS station restored.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load POS stations.',
      invalid_name: 'Enter a POS station name.',
      duplicate_name: 'A POS station with that name already exists in this venue.',
    },
  },
  employees: {
    title: 'Employees',
    loading: 'Loading employees…',
    empty: 'No employees yet.',
    readOnly: 'Read-only',
    status: {
      active: 'Active',
      inactive: 'Inactive',
    },
    user: {
      unlinked: 'No linked user',
      loading: 'Loading users…',
    },
    columns: {
      name: 'Name',
      status: 'Status',
      user: 'Linked user',
      actions: 'Actions',
    },
    fields: {
      name: 'Name',
      user: 'Linked user',
    },
    actions: {
      create: 'Add employee',
      edit: 'Edit',
      activate: 'Activate',
      deactivate: 'Deactivate',
    },
    create: {
      title: 'Add employee',
      description: 'Add a franchise-wide staff record. Linking a login is optional.',
      submit: 'Add employee',
      submitting: 'Adding…',
    },
    edit: {
      title: 'Edit employee',
      description: 'Update the roster record or change the linked login.',
      submit: 'Save changes',
      submitting: 'Saving…',
    },
    toast: {
      created: 'Employee added.',
      updated: 'Employee updated.',
      activated: 'Employee reactivated.',
      deactivated: 'Employee deactivated.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      loadFailed: 'Could not load employees.',
      invalid_name: 'Enter an employee name.',
      linked_user_not_found: 'That user could not be found.',
      user_already_linked: 'That user is already linked to another employee.',
      employee_has_open_shift: 'Close the employee’s open shift before deactivating them.',
    },
  },
  admissions: {
    page: {
      title: 'Admit a child',
      subtitle: 'Find the child, pick the guardian dropping them off, check consent, then admit.',
      selectBoth: 'Select a child and a guardian to continue.',
      pickChildFirst: 'Pick a child first to see their guardians.',
    },
    convertedChild: {
      heading: 'New child from converted check-in',
      hint: 'Confirm the child’s details, then create the profile to continue.',
      firstName: 'Child first name',
      lastName: 'Child last name',
      dateOfBirth: 'Date of birth',
      create: 'Create child profile',
      creating: 'Creating…',
      noPermission: 'You do not have permission to create a child profile.',
      error: 'Could not create the child profile. Please try again.',
    },
    picker: {
      child: 'Child',
      guardian: 'Guardian',
      searchChild: 'Search children',
      searchGuardian: 'Search guardians',
      change: 'Change',
    },
    guardian: {
      noneConsented: 'No guardian has consent for this child here yet.',
      addNew: 'Add a new guardian',
      newHeading: 'New guardian',
      firstName: 'First name',
      lastName: 'Last name',
      save: 'Save guardian',
      cancel: 'Cancel',
    },
    context: {
      label: 'Selected admission context',
      heading: 'Selected family',
      child: 'Child',
      guardian: 'Presenting guardian',
    },
    admit: {
      submit: 'Admit child',
      submitting: 'Admitting…',
      needsConsent: 'Capture a valid consent before admitting.',
    },
    toast: {
      admitted: '{{name}} admitted.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      refused: 'This child cannot be admitted.',
      no_valid_consent: 'No valid consent for this child and guardian. Capture consent first.',
      not_on_open_shift: 'Open a shift at this venue before admitting.',
      offline: 'You are offline. Admitting requires a connection — reconnect and try again.',
    },
  },
  admissionsDesk: {
    title: 'Admissions',
    subtitle: 'Approve staged check-ins or admit arrivals directly from one desk.',
    railLabel: 'Admission paths',
    status: {
      venueConsent: 'Venue-scoped consent',
      ageThreshold: 'Adult threshold enforced',
    },
    gate: {
      title: 'Open a shift first',
      loading: 'Checking your shift…',
      openMyShift: 'Open my shift',
      askManager: 'Ask a manager to open your shift for you.',
      error: 'Couldn’t check your shift status.',
      retry: 'Try again',
    },
    paths: {
      staged: 'Approve staged',
      child: 'Existing child',
      adult: 'Adult walk-in',
    },
    pathDescriptions: {
      staged: 'Review self check-ins waiting for staff.',
      child: 'Pick the child first, then a valid guardian.',
      adult: 'Admit an eligible adult from name and DOB.',
    },
    stagedTabs: {
      label: 'Staged admission views',
      review: 'Review selected',
      recent: 'Recent',
    },
  },
  adultAdmissions: {
    page: {
      title: 'Admit an adult',
      subtitle: 'Enter the customer’s full name and date of birth, then admit.',
    },
    form: {
      fullName: 'Full name',
      dateOfBirth: 'Date of birth',
      overrideReason: 'Age-threshold override reason',
      overrideHint:
        'This date of birth is below the adult age threshold. Recording a reason requires the override permission and is audited.',
      submit: 'Admit adult',
      submitting: 'Admitting…',
    },
    toast: {
      admitted: '{{name}} admitted.',
    },
    errors: {
      required: 'Enter a full name and date of birth.',
      refused: 'This customer cannot be admitted as an adult.',
      adult_admission_requires_override:
        'Below the adult age threshold. Use the child/guardian flow, or have an authorised user record an override reason.',
      not_on_open_shift: 'Open a shift at this venue before admitting.',
      date_of_birth_required: 'A date of birth is required to admit an adult.',
      invalid_name: 'Enter the customer’s full name.',
      offline: 'You are offline. Admitting requires a connection — reconnect and try again.',
    },
  },
  selfCheckInLanding: {
    title: 'Self check-in',
    subtitle: 'Who are you checking in today?',
    child: {
      title: 'Check in a child',
      description: 'Enter the child and guardian details and sign consent.',
    },
    adult: {
      title: 'Check in an adult',
      description: 'Enter your full name and date of birth.',
    },
  },
  selfCheckIn: {
    page: {
      title: 'Welcome! Let’s get you checked in',
      subtitle:
        'Tell us about your child and yourself, then sign the consent. A staff member finishes the check-in.',
    },
    steps: {
      child: 'Child',
      guardian: 'Guardian',
      consent: 'Consent',
    },
    child: {
      heading: 'About the child',
      firstName: 'Child’s first name',
      lastName: 'Child’s last name',
      dateOfBirth: 'Date of birth (optional)',
    },
    guardian: {
      heading: 'About you',
      firstName: 'Your first name',
      lastName: 'Your last name',
      phone: 'Phone (optional)',
      email: 'Email (optional)',
    },
    consent: {
      heading: 'Consent',
      intro: 'Please read and sign to give consent for {{child}} to play here today.',
    },
    actions: {
      next: 'Continue',
      back: 'Back',
      finish: 'Finish',
      finishing: 'Saving…',
      again: 'Start a new check-in',
    },
    done: {
      title: 'All set, thank you!',
      message: 'Please hand the device back to a staff member to complete {{child}}’s admission.',
    },
    errors: {
      required: 'Please fill in the first and last name.',
      blank_signature: 'Add a signature before continuing.',
      signature_too_large: 'The signature image is too large.',
      generic: 'Something went wrong. Please try again.',
    },
  },
  stagedSelfCheckIn: {
    page: {
      title: 'Welcome! Let’s get you checked in',
      subtitle: 'Enter your full name and date of birth. A staff member finishes the check-in.',
    },
    form: {
      fullName: 'Full name',
      dateOfBirth: 'Date of birth',
    },
    actions: {
      submit: 'Check in',
      submitting: 'Submitting…',
      again: 'Start a new check-in',
    },
    done: {
      title: 'All set, thank you!',
      message: 'Please hand the device back to a staff member to finish your check-in.',
    },
    errors: {
      required: 'Please enter your full name and date of birth.',
      generic: 'Something went wrong. Please try again.',
    },
  },
  stagedQueue: {
    title: 'Staged check-ins',
    subtitle: 'Self check-ins waiting for staff approval at this venue.',
    loading: 'Loading queue…',
    empty: 'No staged check-ins waiting.',
    waitedFor: 'Waiting {{duration}}',
    eligibility: {
      ELIGIBLE: 'Eligible',
      CONVERT_TO_CHILD: 'Under age — child flow',
      CONVERT_TO_ADULT: 'Over age — adult flow',
      REQUIRES_OVERRIDE: 'Age unverified',
    },
    type: {
      ADULT: 'Adult',
      CHILD: 'Child',
    },
    errors: {
      generic: 'Could not load the queue. Please try again.',
    },
  },
  admissionsHistory: {
    title: 'Recent admissions',
    subtitle: 'Admitted, rejected and cancelled check-ins at this venue today.',
    loading: 'Loading history…',
    empty: 'Nothing admitted, rejected or cancelled today.',
    redacted: 'Redacted',
    reason: 'Reason: {{reason}}',
    outcome: {
      admitted: 'Admitted',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    kind: {
      adult: 'Adult',
      child: 'Child',
    },
    errors: {
      generic: 'Could not load history. Please try again.',
    },
  },
  stagedReview: {
    title: 'Review check-in',
    subtitle: 'Confirm or correct the details, then approve to admit this adult.',
    selectPrompt: 'Select a staged check-in to review and approve.',
    computedAge: 'Age: {{age}}',
    fields: {
      identity: 'Identity',
      agePath: 'Age path',
      adultPath: 'Adult admission',
      admissionGate: 'Admission gate',
    },
    form: {
      fullName: 'Full name',
      dateOfBirth: 'Date of birth',
      overrideReason: 'Age-threshold override reason',
      overrideHint:
        'This date of birth is below the adult age threshold. Recording a reason requires the override permission and is audited.',
      rejectReason: 'Reject reason',
      submit: 'Approve & admit',
      submitting: 'Approving…',
      convert: 'Convert to child flow',
      converting: 'Converting…',
      cancel: 'Cancel',
      cancelling: 'Cancelling…',
      reject: 'Reject',
      rejecting: 'Rejecting…',
    },
    toast: {
      approved: '{{name}} admitted.',
      converted: '{{name}} moved to the child flow.',
      cancelled: '{{name}} cancelled.',
      rejected: '{{name}} rejected.',
    },
    errors: {
      required: 'Enter a full name and date of birth.',
      refused: 'This check-in cannot be approved.',
      rejectReasonRequired: 'Enter a reason before rejecting this check-in.',
      convertFailed: 'Could not convert this check-in.',
      cancelFailed: 'Could not cancel this check-in.',
      rejectFailed: 'Could not reject this check-in.',
      adult_admission_requires_override:
        'Below the adult age threshold. Use the child/guardian flow, or have an authorised user record an override reason.',
      blank_reject_reason: 'Enter a reason before rejecting this check-in.',
      not_on_open_shift: 'Open a shift at this venue before approving.',
      not_found: 'This check-in is no longer in the queue.',
      offline: 'You are offline. Approving requires a connection — reconnect and try again.',
    },
  },
  childReview: {
    title: 'Review child check-in',
    subtitle: 'Approve to create the child, guardian, consent, and visit, or reject this entry.',
    computedAge: 'Age: {{age}}',
    fields: {
      childName: 'Child',
      dateOfBirth: 'Date of birth',
      agePath: 'Age path',
      childPath: 'Child admission',
      admissionGate: 'Admission gate',
      guardianName: 'Guardian',
      guardianContact: 'Guardian contact',
      consent: 'Consent',
      noDateOfBirth: 'Not provided',
      noContact: 'Not provided',
      consentSigned: 'Signed by {{name}}',
    },
    form: {
      rejectReason: 'Reject reason',
      submit: 'Approve & admit',
      submitting: 'Approving…',
      convert: 'Convert to adult & admit',
      converting: 'Converting…',
      reject: 'Reject',
      rejecting: 'Rejecting…',
    },
    convertHint:
      'This date of birth is at or above the adult age threshold. Converting admits an adult and discards the guardian and consent.',
    toast: {
      approved: '{{name}} admitted.',
      converted: '{{name}} admitted as an adult.',
      rejected: '{{name}} rejected.',
    },
    errors: {
      refused: 'This check-in cannot be approved.',
      rejectReasonRequired: 'Enter a reason before rejecting this check-in.',
      rejectFailed: 'Could not reject this check-in.',
      convertFailed: 'Could not convert this check-in.',
      adult_admission_requires_override:
        'This date of birth is below the adult age threshold — admit through the child flow instead.',
      blank_reject_reason: 'Enter a reason before rejecting this check-in.',
      not_on_open_shift: 'Open a shift at this venue before approving.',
      not_found: 'This check-in is no longer in the queue.',
      offline: 'You are offline. Approving requires a connection — reconnect and try again.',
    },
  },
  consents: {
    title: 'Consent',
    loading: 'Checking consent…',
    signedBy: 'Signed by {{name}}',
    status: {
      VALID: 'Consent on file',
      EXPIRED: 'Consent expired',
      REVOKED: 'Consent revoked',
      none: 'No consent on file yet.',
    },
    capture: {
      label: 'Signature',
      submit: 'Save consent',
      submitting: 'Saving…',
      clear: 'Clear',
    },
    revoke: {
      submit: 'Revoke consent',
      submitting: 'Revoking…',
    },
    toast: {
      captured: 'Consent recorded.',
      revoked: 'Consent revoked.',
    },
    page: {
      title: 'Consent',
      selectBoth: 'Select a child and a guardian to check or capture consent.',
    },
    picker: {
      child: 'Child',
      guardian: 'Guardian',
      searchChild: 'Search children',
      searchGuardian: 'Search guardians',
      change: 'Change',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      blank_signature: 'Add a signature before saving.',
      unsupported_signature_type: 'The signature must be a PNG image.',
      signature_too_large: 'The signature image is too large.',
      not_found: 'That child or guardian no longer exists.',
      consent_already_revoked: 'That consent has already been revoked.',
    },
  },
  shifts: {
    title: 'Shifts',
    loading: 'Loading shifts…',
    fields: {
      venue: 'Venue',
      employee: 'Employee',
      state: 'State',
      from: 'From',
      to: 'To',
    },
    actions: {
      open: 'Open shift',
      close: 'Close shift',
      correct: 'Correct shift',
      saveCorrection: 'Save correction',
      cancel: 'Cancel',
    },
    open: {
      selectEmployee: 'Select an employee',
    },
    correct: {
      openedAt: 'Opened at',
      closedAt: 'Closed at',
    },
    self: {
      title: 'My shift',
      start: 'Start my shift',
      end: 'End my shift',
    },
    overview: {
      title: 'My shift',
      checkIn: 'Check in',
      checkOut: 'Check out',
      onShiftHere: 'You are checked in here.',
      offShiftHere: 'You are not checked in here.',
      duration: '{{hours}}h {{minutes}}m',
      checkedIn: 'Checked in.',
      checkedOut: 'Checked out.',
      transfer: {
        title: 'Already checked in elsewhere',
        body: 'You are currently checked in at {{venueName}}. Check out there and check in here?',
        confirm: 'Check in here',
        cancel: 'Cancel',
      },
    },
    current: {
      title: 'Current open shifts',
      empty: 'No one is currently on shift at this venue.',
    },
    history: {
      title: 'Shift history',
      empty: 'No shifts match the selected filters.',
    },
    columns: {
      employee: 'Employee',
      venue: 'Venue',
      opened: 'Opened',
      closed: 'Closed',
      actors: 'Opened / closed by',
      actions: 'Actions',
    },
    state: {
      open: 'Open',
      closed: 'Closed',
    },
    toast: {
      opened: 'Shift opened.',
      closed: 'Shift closed.',
      corrected: 'Shift corrected.',
    },
    errors: {
      generic: 'Something went wrong. Please try again.',
      inactive_employee: 'Inactive employees cannot open shifts.',
      employee_already_on_shift: 'That employee already has an open shift.',
      shift_already_closed: 'That shift has already been closed.',
      no_linked_employee: 'Your account is not linked to an employee, so you cannot start a shift.',
      not_found: 'The shift, employee, or venue could not be found.',
      shift_correction_empty: 'Enter a new start or end time to correct the shift.',
      shift_correction_on_open_shift: 'An open shift has no end time to correct yet.',
      shift_correction_future: 'A corrected time cannot be in the future.',
      shift_correction_order: 'The start time must come before the end time.',
    },
  },
  auth: {
    login: {
      title: 'Sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
    },
    setup: {
      title: 'Set up Amuze',
      subtitle: 'Create the first administrator account to finish installation.',
      token: 'Setup token',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm password',
      submit: 'Create administrator',
      submitting: 'Creating…',
      errors: {
        tokenRequired: 'Setup token is required.',
        emailInvalid: 'Enter a valid email address.',
        passwordTooShort: 'Password must be at least 12 characters.',
        passwordMismatch: 'Passwords do not match.',
        generic: 'Something went wrong. Please try again.',
      },
      alreadyConfigured: {
        title: 'Already configured',
        body: 'This system has already been set up. Sign in with the administrator account.',
        link: 'Sign in',
      },
    },
    changePassword: {
      title: 'Change password',
      forced: 'You must set a new password before continuing.',
      current: 'Current password',
      new: 'New password',
      confirm: 'Confirm new password',
      submit: 'Update password',
      submitting: 'Updating…',
      errors: {
        invalidCurrent: 'Current password is incorrect.',
        tooShort: 'New password must be at least 12 characters.',
        mismatch: 'Passwords do not match.',
        generic: 'Something went wrong. Please try again.',
      },
    },
    logout: 'Sign out',
    accountMenu: 'Account menu',
    errors: {
      invalidCredentials: 'Invalid email or password.',
      generic: 'Something went wrong. Please try again.',
    },
    forbidden: {
      title: 'Forbidden',
      body: 'You do not have permission to view this page.',
    },
  },
  audit: {
    title: 'Audit log',
    loading: 'Loading audit events…',
    empty: 'No audit events match these filters.',
    system: 'System',
    filters: {
      actor: 'Actor',
      actions: 'Actions',
      targetType: 'Target type',
      anyTargetType: 'Any target type',
      target: 'Target',
      venue: 'Venue',
      anyVenue: 'Any venue',
      from: 'From',
      to: 'To',
      apply: 'Apply',
      reset: 'Reset',
    },
    columns: {
      time: 'Time',
      actor: 'Actor',
      action: 'Action',
      target: 'Target',
      venue: 'Venue',
    },
    row: {
      expand: 'Details',
      collapse: 'Hide',
    },
    payload: {
      before: 'Before',
      after: 'After',
      metadata: 'Metadata',
      empty: 'None',
    },
    pagination: {
      summary: 'Page {{page}} of {{totalPages}} · {{total}} events',
      prev: 'Previous',
      next: 'Next',
    },
    errors: {
      loadFailed: 'Could not load the audit log.',
    },
    actionLabels: {
      'system.bootstrapped': 'System bootstrapped',
      'auth.login_success': 'Login succeeded',
      'auth.login_failure': 'Login failed',
      'auth.logout': 'Logged out',
      'auth.session_revoked': 'Session revoked',
      'user.created': 'User created',
      'user.updated': 'User updated',
      'user.activated': 'User activated',
      'user.deactivated': 'User deactivated',
      'user.password_changed_by_self': 'Password changed by self',
      'user.password_reset_by_admin': 'Password reset by admin',
      'user.pos_pin_changed_by_self': 'POS PIN changed by self',
      'user.pos_pin_regenerated_by_admin': 'POS PIN regenerated by admin',
      'user.venue_pos_pin_issued': 'Venue POS PIN issued',
      'user.venue_pos_pin_regenerated': 'Venue POS PIN regenerated',
      'user.venue_pos_pin_revoked': 'Venue POS PIN revoked',
      'role.created': 'Role created',
      'role.updated': 'Role updated',
      'role.deleted': 'Role deleted',
      'role.permissions_changed': 'Role permissions changed',
      'role_assignment.created': 'Role assignment created',
      'role_assignment.updated': 'Role assignment updated',
      'role_assignment.revoked': 'Role assignment revoked',
      'brand.updated': 'Brand updated',
      'brand.logo_updated': 'Brand logo updated',
      'country_policy.created': 'Country policy created',
      'country_policy.updated': 'Country policy updated',
      'country_policy.enabled_changed': 'Country policy enabled changed',
      'venue.created': 'Venue created',
      'venue.updated': 'Venue updated',
      'venue.locale_changed': 'Venue locale changed',
      'venue_booking_settings.updated': 'Venue booking settings updated',
      'venue_operating_hours.updated': 'Venue operating hours updated',
      'room.created': 'Room created',
      'room.updated': 'Room updated',
      'capacity_block.created': 'Capacity block created',
      'capacity_block.updated': 'Capacity block updated',
      'capacity_block.deleted': 'Capacity block deleted',
      'employee.created': 'Employee created',
      'employee.updated': 'Employee updated',
      'shift.opened': 'Shift opened',
      'shift.closed': 'Shift closed',
      'shift.corrected': 'Shift corrected',
      'sale.completed': 'Sale completed',
      'customer_profile.created': 'Customer profile created',
      'customer_profile.updated': 'Customer profile updated',
      'discount_code.created': 'Discount code created',
      'discount.applied': 'Discount applied',
      'waiver_template.created': 'Waiver template created',
      'waiver_template.version_created': 'Waiver template version created',
      'venue.waiver_template_assigned': 'Venue waiver template assigned',
      'waiver_record.signed': 'Waiver record signed',
      'waiver_record.revoked': 'Waiver record revoked',
      'pass.created': 'Pass created',
      'pass.updated': 'Pass updated',
      'pass.activated': 'Pass activated',
      'pass.deactivated': 'Pass deactivated',
      'mvp_definition.created': 'Multi-Visit Pass Product Definition created',
      'mvp_definition.updated': 'Multi-Visit Pass Product Definition updated',
      'mvp_definition.activated': 'Multi-Visit Pass Product Definition activated',
      'mvp_definition.deactivated': 'Multi-Visit Pass Product Definition deactivated',
      'mvp_definition.deleted': 'Multi-Visit Pass Product Definition deleted',
      'mvp.sold': 'Multi-Visit Pass sold',
      'mvp_card.bound': 'MVP card bound',
      'mvp_card.deactivated': 'MVP card deactivated',
      'mvp_card.numbers_generated': 'MVP card numbers generated',
      'mvp.visit_redeemed': 'MVP visit redeemed',
      'mvp.visit_undone': 'MVP visit undone',
      'standard_pass_visit.undone': 'Standard Pass visit undone',
      'mvp.voided': 'Multi-Visit Pass voided',
      'mvp.refunded': 'Multi-Visit Pass refunded',
      'booking.created': 'Booking created',
      'booking.updated': 'Booking updated',
      'booking.cancelled': 'Booking cancelled',
      'booking.paid': 'Booking paid',
      'booking_item_unit.redeemed': 'Booking item unit redeemed',
      'booking_item_unit.cancelled': 'Booking item unit cancelled',
      'booking_item_unit.refunded': 'Booking item unit refunded',
      'booking_item_unit.waiver_linked': 'Booking item unit waiver linked',
      'booking_item_unit.waiver_unlinked': 'Booking item unit waiver unlinked',
      'booking_item_unit.waiver_replaced': 'Booking item unit waiver replaced',
      'inventory_item.created': 'Inventory item created',
      'inventory_item.updated': 'Inventory item updated',
      'pos_installation.created': 'POS installation created',
      'pos_installation.enrolled': 'POS installation enrolled',
      'pos_installation.revoked': 'POS installation revoked',
      'pos_device.created': 'POS station created',
      'pos_device.updated': 'POS station updated',
      'pos_device.allocated': 'POS station allocated',
      'pos_device.released': 'POS station released',
      'pos_device.taken_over': 'POS station taken over',
    } satisfies Record<AuditActionCode, string>,
    targetTypeLabels: {
      system: 'System',
      auth: 'Authentication',
      user: 'User',
      role: 'Role',
      role_assignment: 'Role assignment',
      brand: 'Brand',
      country_policy: 'Country policy',
      venue: 'Venue',
      venue_booking_settings: 'Venue booking settings',
      venue_operating_hours: 'Venue operating hours',
      room: 'Room',
      capacity_block: 'Capacity block',
      employee: 'Employee',
      shift: 'Shift',
      inventory_item: 'Inventory item',
      price_list: 'Price list',
      game: 'Game',
      play_session: 'Play session',
      sale: 'Sale',
      waiver_template: 'Waiver template',
      waiver_template_version: 'Waiver template version',
      venue_waiver_template_assignment: 'Venue waiver template assignment',
      waiver_record: 'Waiver record',
      discount_code: 'Discount code',
      discount: 'Discount',
      pass: 'Pass',
      mvp_definition: 'Multi-Visit Pass Product Definition',
      mvp: 'Multi-Visit Pass',
      multi_visit_pass: 'Multi-Visit Pass',
      mvp_card: 'MVP card',
      mvp_visit: 'Multi-Visit Pass visit',
      standard_pass_visit: 'Standard Pass visit',
      booking: 'Booking',
      booking_item_unit: 'Booking item unit',
      pos_installation: 'POS installation',
      pos_device: 'POS station',
      customer_profile: 'Customer profile',
    },
  },
} as const;

export type CommonResource = typeof commonEnUS;
